import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Package, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { MaterialFormDialog, type MaterialRecord } from "@/components/materials/MaterialFormDialog";
import { useLocaleFormat } from "@/lib/i18n-format";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

export const Route = createFileRoute("/_app/materials/")({
  component: MaterialsIndex,
});

function MaterialsIndex() {
  const { t } = useTranslation();
  const { company, hasRole } = useAuth();
  const { formatMoney } = useLocaleFormat();
  const isAdmin = hasRole("admin");
  const [rows, setRows] = useState<MaterialRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MaterialRecord | null>(null);
  const [deleting, setDeleting] = useState<MaterialRecord | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("materials")
      .select(
        "id, sku, name, description, category, unit, unit_cost, stock_level, reorder_level, currency, is_active, notes, company_id",
      )
      .order("name", { ascending: true });
    setLoading(false);
    if (error) return toast.error(error.message);
    setRows((data ?? []) as MaterialRecord[]);
  }

  useEffect(() => {
    load();
  }, []);

  const scopedRows = useFilterByOwnerCompany(
    rows as Array<MaterialRecord & { company_id?: string | null }>,
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scopedRows;
    return scopedRows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.sku ?? "").toLowerCase().includes(q) ||
        (r.category ?? "").toLowerCase().includes(q),
    );
  }, [scopedRows, search]);

  const stats = useMemo(() => {
    const totalValue = rows.reduce(
      (acc, r) => acc + Number(r.unit_cost ?? 0) * Number(r.stock_level ?? 0),
      0,
    );
    const lowStock = rows.filter(
      (r) => Number(r.stock_level ?? 0) <= Number(r.reorder_level ?? 0),
    ).length;
    return { count: rows.length, totalValue, lowStock };
  }, [rows]);

  async function onDelete() {
    if (!deleting) return;
    const { error } = await supabase.from("materials").delete().eq("id", deleting.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("materials.deleted"));
    setDeleting(null);
    load();
  }

  const currency = company?.currency ?? "USD";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("materials.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("materials.subtitle")}</p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("materials.newMaterial")}
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("materials.statItems")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{stats.count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("materials.statStockValue")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{formatMoney(stats.totalValue, currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("materials.statLowStock")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{stats.lowStock}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("materials.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("materials.loading")}
            </p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
              <Package className="h-8 w-8" />
              <p className="text-sm">{t("materials.empty")}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("materials.addFirst")}
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("materials.table.name")}</TableHead>
                    <TableHead>{t("materials.table.sku")}</TableHead>
                    <TableHead>{t("materials.table.category")}</TableHead>
                    <TableHead className="text-right">{t("materials.table.unitCost")}</TableHead>
                    <TableHead className="text-right">{t("materials.table.stock")}</TableHead>
                    <TableHead className="w-[100px] text-right">
                      {t("materials.table.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const low =
                      Number(r.stock_level ?? 0) <= Number(r.reorder_level ?? 0) &&
                      Number(r.reorder_level ?? 0) > 0;
                    return (
                      <TableRow key={r.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CompanyBadge companyId={r.company_id} />
                            <div>
                              <div className="font-medium">{r.name}</div>
                              {r.description && (
                                <p className="line-clamp-1 text-xs text-muted-foreground">
                                  {r.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.sku ?? "—"}</TableCell>
                        <TableCell>
                          {r.category ? (
                            <Badge variant="secondary">{r.category}</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatMoney(Number(r.unit_cost), r.currency)}
                          <div className="text-xs text-muted-foreground">/ {r.unit}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {low && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
                            <span className={low ? "font-medium text-destructive" : ""}>
                              {Number(r.stock_level).toFixed(2)} {r.unit}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditing(r);
                                setDialogOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {isAdmin && (
                              <Button variant="ghost" size="icon" onClick={() => setDeleting(r)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <MaterialFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        material={editing}
        onSaved={load}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("materials.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("materials.deleteDescription", { name: deleting?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("materials.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>{t("materials.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
